<template>
  <OnboardingFeatureView
    ref="elRef"
  >
    <p>
      Check who’s in or out without the chaos of a group chat
    </p>
    <PlayerList
      class="OnboardingStatusView__playerList"
      title="In"
      v-bind="{players, statusMap, subRequestMap}"
      :isClickable="false"
      isActive
    />
    <p>
      and automatically keep track of numbers
    </p>
    <RunStatRow
      class="OnboardingStatusView__statRow"
      v-bind="isIntersecting ? {
        inCount: players.length,
        maybeCount: 2,
        openSpotCount: 5,
        neededPlayerCount: 0,
      } : {
        inCount: 0,
        maybeCount: 0,
        openSpotCount: 0,
        neededPlayerCount: 0,
      }"
    />
  </OnboardingFeatureView>
</template>

<script setup lang="ts">
import PlayerList from "@/components/PlayerList.vue";
import RunStatRow from "@/components/RunStatRow.vue";
import useIntersectionDetection from "@/composables/useIntersectionDetection.ts";
import { topPlayerMap } from "@shared/lib/constants/players.ts";
import { Theme, themeMap } from "@shared/lib/constants/themes.ts";
import { TablesInsert } from "@shared/lib/database/database.types.ts";
import { PlayerSummary, SubRequestWithAssociations } from "@shared/lib/database/query.types.ts";
import { computed, ref, useTemplateRef } from "vue";
import OnboardingFeatureView from "./OnboardingFeatureView.vue";

const player = defineModel<TablesInsert<"players">>("player", {
  required: true,
});

const elRef = useTemplateRef<InstanceType<typeof OnboardingFeatureView>>("elRef");
const isIntersecting = ref(false);

const theme = computed(() => {
  return themeMap.get((player.value?.theme ?? Theme.LosAngelesLakers) as keyof typeof Theme);
});

const topPlayer = computed(() => {
  return topPlayerMap.get(theme.value?.slug as keyof typeof Theme);
});

const players = computed(() => {
  return [
    ...(theme.value?.league === "NBA"
      ? [
        Theme.NewYorkKnicks,
        Theme.SanAntonioSpurs,
        Theme.LosAngelesLakers,
      ]
      : [
        Theme.DallasWings,
        Theme.IndianaFever,
        Theme.AtlantaDream,
      ]),
  ].reduce((acc, theme) => {
    if (acc.length === 3) return acc;
    if (!acc.find((player) => player.theme === theme)) {
      acc.push({
        id: acc.length.toString(),
        name: topPlayerMap.get(theme)?.name ?? "",
        avatar_storage_path: null,
        theme,
      });
    }

    return acc;
  }, [
    {
      id: "0",
      name: topPlayer.value?.name ?? "",
      theme: theme.value?.slug as keyof typeof Theme,
      avatar_storage_path: null,
    },
  ] as Array<PlayerSummary>);
});

const statusMap = ref(new Map([
  [
    "0",
    {
      run_id: "1",
      player_id: "0",
      probability: 1,
      note: "Gonna be 15 min late",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ],
]));

const subRequestMap = computed(() => {
  return new Map([
    [
      "2",
      {
        id: "2",
        probability: 1,
        created_at: new Date().toISOString(),
        run_id: "1",
        player_id: "2",
        accepted_at: new Date().toISOString(),
        player: players.value[2],
        run_invite_id: "1",
        run_invite: {
          id: "1",
          player_id: "2",
          run_id: "1",
          run_invite_id: "1",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          player: {
            id: "4",
            name: theme.value?.league === "NBA" ? "Adam Silver" : "Holly Rowe",
            theme: Theme.SanAntonioSpurs,
            avatar_storage_path: null,
          },
        },
      } as unknown as SubRequestWithAssociations,
    ],
  ]);
});

useIntersectionDetection(elRef, (_isIntersecting) => {
  if (_isIntersecting) {
    isIntersecting.value = true;
  }
});
</script>

<style lang="scss" scoped>
.OnboardingStatusView__playerList {
  outline: solid 3px var(--themeColorWhite);
}

.OnboardingStatusView__statRow {
  overflow: hidden;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 6px;
  outline: solid 3px var(--themeColorWhite);
}

.OnboardingStatusView__playerList,
.OnboardingStatusView__statRow {
  width: 100%;
  max-width: 340px;
  margin-inline: auto;
}
</style>
